import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useBusiness } from '../../contexts/BusinessContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import {
  TrendingUp, FileText, Package, Users, Store, Plus, AlertTriangle, AlertCircle, Clock,
  MapPin, IndianRupee, Navigation, CheckCircle, Target, LayoutDashboard
} from 'lucide-react';
import ListPageHeader from '../../components/shared/ListPageHeader';
import { staffDashboardAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';

const StaffDashboard = () => {
  const { activeBusiness } = useBusiness();
  const { user, loading: authLoading } = useAuth();
  const { isOwner, isStaff } = usePermissions();
  const { businessId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState({
    todaySales: 0,
    todayInvoices: 0,
    todayCollections: 0,
    draftInvoices: 0,
    pendingVisits: 0,
    completedVisits: 0,
    shiftActive: false,
    recentBills: [],
    outstandingTotal: 0,
    receiptsToday: 0,
  });

  useEffect(() => {
    if (!authLoading && user) {
      if (isOwner) {
        console.warn('[StaffDashboard] Owner user trying to access staff dashboard - redirecting');
        navigate(`/business/${businessId}/dashboard/owner`, { replace: true });
        return;
      }
    }
  }, [authLoading, user, businessId, navigate, isOwner]);

  useEffect(() => {
    if (businessId && !isOwner) {
      fetchDashboardData();
    }
  }, [businessId, isOwner]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: stats } = await staffDashboardAPI.getStats();

      const invoicesRes = await staffDashboardAPI.getRecentInvoices({ limit: 10 }).catch(() => ({ data: { invoices: [] } }));
      const invoicesData = invoicesRes.data;

      setDashboardData({
        todaySales: stats.today_sales || 0,
        todayInvoices: stats.today_invoices || 0,
        todayCollections: stats.today_collections || 0,
        draftInvoices: stats.draft_invoices || 0,
        pendingVisits: stats.pending_visits || 0,
        completedVisits: stats.completed_visits || 0,
        inProgressVisits: stats.in_progress_visits || 0,
        shiftActive: stats.shift_active || false,
        shiftId: stats.shift_id,
        recentBills: invoicesData.invoices || [],
        outstandingTotal: stats.outstanding_total || 0,
        receiptsToday: stats.receipts_today || 0,
      });
    } catch (error) {
      setError(getApiErrorMessage(error, 'Failed to load dashboard data. Please try again.'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatCompactCurrency = (amount) => {
    if (!amount) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount.toFixed(0)}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '---';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const totalVisits = dashboardData.pendingVisits + dashboardData.completedVisits + (dashboardData.inProgressVisits || 0);
  const visitCompletion = totalVisits > 0 ? Math.round((dashboardData.completedVisits / totalVisits) * 100) : 0;

  const quickActions = [
    { label: 'Start Visit', icon: Navigation, path: '/tracking/my-visits', color: 'bg-blue-500' },
    { label: 'Record Collection', icon: IndianRupee, path: '/finance/my-collections', color: 'bg-emerald-500' },
    { label: 'Create Receipt', icon: Store, path: '/pos', color: 'bg-teal-500' },
    { label: 'My Retailers', icon: Users, path: '/tracking/my-retailers', color: 'bg-purple-500' },
  ];

  const getStatusBadge = (status) => {
    const upper = (status || '').toUpperCase();
    switch (upper) {
      case 'FINAL':
        return { label: 'Final', className: 'bg-emerald-50 text-emerald-700' };
      case 'DRAFT':
        return { label: 'Draft', className: 'bg-amber-50 text-amber-700' };
      case 'SENT':
        return { label: 'Sent', className: 'bg-blue-50 text-blue-700' };
      case 'PARTIALLY_PAID':
        return { label: 'Partial', className: 'bg-orange-50 text-orange-700' };
      case 'PAID':
        return { label: 'Paid', className: 'bg-emerald-50 text-emerald-700' };
      default:
        return { label: status || '---', className: 'bg-slate-100 text-slate-600' };
    }
  };

  return (
    <div className="h-full flex flex-col" data-testid="staff-dashboard-page">
      {/* Premium Header */}
      <ListPageHeader
        title="My Dashboard"
        subtitle={activeBusiness?.name ? `Welcome back • ${activeBusiness.name}` : 'Your daily activity overview'}
        stats={[
          { icon: IndianRupee, label: 'Sales Today', value: formatCompactCurrency(dashboardData.todaySales), color: 'emerald' },
          { icon: CheckCircle, label: 'Visits', value: `${dashboardData.completedVisits}/${totalVisits}`, color: 'teal' },
          { icon: TrendingUp, label: 'Collections', value: formatCompactCurrency(dashboardData.todayCollections), color: 'blue' },
          { icon: Target, label: 'Outstanding', value: formatCompactCurrency(dashboardData.outstandingTotal), color: dashboardData.outstandingTotal > 0 ? 'rose' : 'slate' },
          { icon: FileText, label: 'Receipts', value: dashboardData.receiptsToday, color: 'purple' },
        ]}
        primaryAction={{
          label: 'Start Visit',
          icon: Navigation,
          onClick: () => navigate(`/business/${businessId}/tracking/my-visits`)
        }}
      />

      {/* Content Area */}
      <div className="flex-1 overflow-auto px-2 pb-2 pt-2">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
              <p className="text-[13px] text-slate-500 font-normal">Loading dashboard...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center">
              <div className="w-14 h-14 bg-red-50 rounded-xl flex items-center justify-center mb-4">
                <AlertCircle className="w-7 h-7 text-red-400" />
              </div>
              <p className="text-[14px] font-medium text-slate-700">Failed to load dashboard</p>
              <p className="text-[13px] text-slate-400 mt-1">{error}</p>
              <button
                onClick={fetchDashboardData}
                className="mt-4 px-4 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700"
              >
                Retry
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Quick Actions */}
            <div className="bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
                <h3 className="text-[14px] font-semibold text-slate-700">Quick Actions</h3>
                <p className="text-[11px] text-slate-500 mt-0.5">Your frequently used features</p>
              </div>
              <div className="p-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {quickActions.map((action, index) => {
                    const Icon = action.icon;
                    return (
                      <Link key={index} to={`/business/${businessId}${action.path}`}>
                        <div className="flex flex-col items-center p-4 bg-slate-50 hover:bg-teal-50 border border-slate-200 hover:border-teal-200 rounded-lg transition-all cursor-pointer group">
                          <div className={`p-2.5 rounded-lg ${action.color} text-white mb-2 group-hover:scale-110 transition-transform`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <span className="text-[12px] font-medium text-slate-700 group-hover:text-teal-700 text-center">
                            {action.label}
                          </span>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Visit Progress Card */}
            <div className="bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-teal-50/50 to-transparent flex items-center justify-between">
                <div>
                  <h3 className="text-[14px] font-semibold text-slate-700">Today&apos;s Progress</h3>
                  <p className="text-[11px] text-slate-500 mt-0.5">Visit completion status</p>
                </div>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${
                  visitCompletion >= 80 ? 'bg-emerald-50 text-emerald-700' :
                  visitCompletion >= 50 ? 'bg-amber-50 text-amber-700' :
                  'bg-slate-100 text-slate-600'
                }`}>
                  {visitCompletion}% Complete
                </span>
              </div>
              <div className="p-4">
                <div className="w-full bg-slate-100 rounded-full h-2.5 mb-3">
                  <div
                    className={`h-2.5 rounded-full transition-all ${
                      visitCompletion >= 80 ? 'bg-emerald-500' :
                      visitCompletion >= 50 ? 'bg-amber-500' :
                      'bg-slate-400'
                    }`}
                    style={{ width: `${visitCompletion}%` }}
                  ></div>
                </div>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-[20px] font-semibold text-emerald-600">{dashboardData.completedVisits}</p>
                    <p className="text-[11px] text-slate-500">Completed</p>
                  </div>
                  <div>
                    <p className="text-[20px] font-semibold text-amber-600">{dashboardData.inProgressVisits || 0}</p>
                    <p className="text-[11px] text-slate-500">In Progress</p>
                  </div>
                  <div>
                    <p className="text-[20px] font-semibold text-slate-600">{dashboardData.pendingVisits}</p>
                    <p className="text-[11px] text-slate-500">Pending</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Bills Table */}
            <div className="bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                <div>
                  <h3 className="text-[14px] font-semibold text-slate-700">Recent Bills</h3>
                  <p className="text-[11px] text-slate-500 mt-0.5">Your latest sales invoices</p>
                </div>
                <Link to={`/business/${businessId}/sales/invoices`} className="text-[12px] text-teal-600 hover:text-teal-700 font-medium">
                  View All
                </Link>
              </div>

              {dashboardData.recentBills.length === 0 ? (
                <div className="py-12 text-center">
                  <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <FileText className="w-6 h-6 text-slate-400" />
                  </div>
                  <p className="text-[14px] font-medium text-slate-600">No invoices yet</p>
                  <p className="text-[12px] text-slate-500 mt-1">Create your first invoice to get started</p>
                  <Link to={`/business/${businessId}/sales/invoices/new`}>
                    <button className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all text-[13px] font-medium">
                      <Plus className="w-4 h-4" />
                      Create Invoice
                    </button>
                  </Link>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="tl-table">
                      <thead>
                        <tr>
                          <th className="tl-th tl-th-teal" style={{ width: '20%' }}>Invoice No</th>
                          <th className="tl-th tl-th-teal" style={{ width: '30%' }}>Customer</th>
                          <th className="tl-th tl-th-teal" style={{ width: '15%' }}>Date</th>
                          <th className="tl-th tl-th-teal tl-th-right" style={{ width: '20%' }}>Amount</th>
                          <th className="tl-th tl-th-teal" style={{ width: '15%' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboardData.recentBills.slice(0, 5).map((bill, index) => {
                          const badge = getStatusBadge(bill.status);
                          return (
                            <tr key={bill.id} className={`tl-tr hover:bg-teal-50/40 ${index % 2 === 0 ? '' : 'bg-[#FAFBFC]'}`}>
                              <td className="tl-td tl-cell-primary">{bill.invoice_number}</td>
                              <td className="tl-td tl-cell-regular">{bill.party_name || '---'}</td>
                              <td className="tl-td tl-cell-regular">{formatDate(bill.invoice_date)}</td>
                              <td className="tl-td tl-td-right">
                                <span className="tl-cell-amount">₹{(bill.grand_total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                              </td>
                              <td className="tl-td">
                                <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${badge.className}`}>
                                  {badge.label}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="px-4 py-3 bg-slate-50/80 border-t border-slate-100 text-[12px] text-slate-500">
                    Showing {Math.min(dashboardData.recentBills.length, 5)} of {dashboardData.recentBills.length} invoices
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StaffDashboard;
